/**
 * Trading Terminal MVP
 * Bloomberg-style live ticker + Auto-Bot deployment hub + Broker bridge cards
 * + Trading Education hook. Designed to give the user the explicit visual
 * confirmation that auto-trading bots exist, are deployable, and are alive.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Activity, Radio, Zap, ShieldCheck, ShieldAlert, Power, Cpu, GraduationCap,
  TrendingUp, TrendingDown, Wifi, WifiOff, BookOpen, Sparkles, Bitcoin,
  IndianRupee, BarChart3, ArrowRight, Lock, CheckCircle2, AlertTriangle,
  Rocket, Brain, Target, Crown,
} from 'lucide-react';
import api from '../utils/api';

// ----- Auto-Bot blueprint (Scalper / Swing / Options / Crypto DCA) -----
const AUTO_BOTS = [
  {
    id: 'scalper',
    label: 'Scalper Bot',
    tagline: 'Mean-reversion scalper · RSI + Bollinger',
    icon: Zap,
    accent: 'from-emerald-500 to-teal-500',
    border: 'border-emerald-500/40',
    glow: 'shadow-emerald-500/30',
    strategy_id: 'mean_reversion',
    asset_class: 'stocks',
    capital: 200000,
    cadence: '1m candles',
    risk: 'Low–Medium',
    instrument: 'NSE Liquid Caps',
  },
  {
    id: 'swing',
    label: 'Swing Bot',
    tagline: 'MACD trend-rider · 3–10 day holds',
    icon: TrendingUp,
    accent: 'from-amber-500 to-orange-500',
    border: 'border-amber-500/40',
    glow: 'shadow-amber-500/30',
    strategy_id: 'macd_swing',
    asset_class: 'stocks',
    capital: 300000,
    cadence: 'Daily / 15m',
    risk: 'Medium',
    instrument: 'Large-Cap Equities',
  },
  {
    id: 'options',
    label: 'Options Bot',
    tagline: '9:20 AM Delta-Neutral · BankNifty',
    icon: Crown,
    accent: 'from-violet-500 to-purple-600',
    border: 'border-violet-500/40',
    glow: 'shadow-violet-500/30',
    strategy_id: 'delta_neutral_920',
    asset_class: 'fno',
    capital: 500000,
    cadence: 'Intraday (09:20–15:15)',
    risk: 'High',
    instrument: 'BANKNIFTY / NIFTY',
    pro: true,
  },
  {
    id: 'crypto_dca',
    label: 'Crypto DCA Bot',
    tagline: 'Grid Matrix · BTC / ETH oscillation',
    icon: Bitcoin,
    accent: 'from-yellow-500 to-amber-600',
    border: 'border-yellow-500/40',
    glow: 'shadow-yellow-500/30',
    strategy_id: 'grid_matrix',
    asset_class: 'crypto',
    capital: 100000,
    cadence: '5m grid',
    risk: 'Medium',
    instrument: 'BTC-USDT / ETH-USDT',
  },
];

// ----- Tracked tickers in the top ribbon -----
const TICKER_SYMBOLS = ['NIFTY', 'BANKNIFTY', 'RELIANCE', 'TCS', 'HDFCBANK', 'INFY', 'BTC', 'ETH'];

const fmt = (n, d = 2) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString('en-IN', { maximumFractionDigits: d, minimumFractionDigits: d });
};

const TradingTerminal = ({
  ticker,
  bots = [],
  brokers = [],
  status,
  walletBalance,
  mySubscription,
  liveReadiness,
  setActiveTab,
  reloadBots,
}) => {
  const [deploying, setDeploying] = useState(null);
  const [sparklines, setSparklines] = useState({});  // strategy_id -> [cumulative pnl points]
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);

  const runDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const r = await api.get('/algotrading/live/diagnostics');
      setDiag(r.data);
      setDiagOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not run diagnostics');
    } finally {
      setDiagLoading(false);
    }
  };

  // Fetch 24h sparkline data per strategy and refresh every 60s
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await api.get('/algotrading/bot-sparklines?hours=24&buckets=12');
        if (alive) setSparklines(r.data?.by_strategy || {});
      } catch (e) { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Plan-level bot quota (Monthly Starter = 2, Pro = higher). Fallback 2.
  const planBotLimit = mySubscription?.max_bots
    || mySubscription?.bot_limit
    || mySubscription?.tier?.max_bots
    || 2;
  const quotaReached = bots.length >= planBotLimit;

  // Merge stocks + fno into a flat ticker map for the ribbon
  const tickerMap = useMemo(() => {
    const out = {};
    if (ticker?.stocks) Object.entries(ticker.stocks).forEach(([k, v]) => { out[k] = v; });
    if (ticker?.fno) Object.entries(ticker.fno).forEach(([k, v]) => { out[k] = v; });
    return out;
  }, [ticker]);

  const wsLive = ticker?.status === 'connected';

  // ----- Stats -----
  const activeBots = bots.filter(b => b.is_active).length;
  const totalBots = bots.length;
  const totalCapital = bots.reduce((s, b) => s + (b.capital_allocated || 0), 0);
  const todayPnl = bots.reduce((s, b) => s + (b.today_pnl || b.realized_pnl || 0), 0);

  // ----- Deploy bot -----
  const deployBot = async (bp) => {
    if (bp.pro && !mySubscription) {
      toast.error('Options Bot needs an active Pro subscription. Visit Subscription tab.');
      setActiveTab && setActiveTab('plans');
      return;
    }
    if (quotaReached) {
      toast.error(`Plan limit reached (${planBotLimit} bots). Upgrade to deploy more.`);
      setActiveTab && setActiveTab('plans');
      return;
    }
    setDeploying(bp.id);
    try {
      const payload = {
        strategy_id: bp.strategy_id,
        mode: 'paper',
        asset_class: bp.asset_class,
        capital_allocated: bp.capital,
      };
      if (bp.asset_class === 'fno') {
        payload.custom_params = { instrument: 'BANKNIFTY' };
        payload.fno_lots = 1;
      }
      await api.post('/algotrading/bots', payload);
      toast.success(`${bp.label} deployed in Paper mode ✓`);
      reloadBots && reloadBots();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to deploy ${bp.label}`);
    } finally {
      setDeploying(null);
    }
  };

  // ----- Find existing bot for a blueprint -----
  const findExistingBot = (bp) => bots.find(b => b.strategy_id === bp.strategy_id);

  return (
    <div className="space-y-6" data-testid="trading-terminal">
      {/* =================== LIVE TICKER RIBBON =================== */}
      <div
        className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-r from-slate-900 via-slate-950 to-slate-900 shadow-lg shadow-emerald-500/10"
        data-testid="terminal-ticker-ribbon"
      >
        <div className="flex items-center gap-3 px-4 py-2 border-b border-white/5 bg-black/40">
          <div className="flex items-center gap-2">
            <span className={`relative inline-flex w-2 h-2 rounded-full ${wsLive ? 'bg-emerald-400' : 'bg-amber-400'}`}>
              {wsLive && <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75"></span>}
            </span>
            <span className={`text-[11px] font-bold tracking-widest uppercase ${wsLive ? 'text-emerald-300' : 'text-amber-300'}`}>
              {wsLive ? 'Live Feed · WebSocket' : 'Reconnecting…'}
            </span>
          </div>
          <button
            onClick={runDiagnostics}
            disabled={diagLoading}
            data-testid="terminal-run-diagnostics"
            className="ml-auto text-[11px] font-bold px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-200 hover:bg-rose-500/25 flex items-center gap-1.5 disabled:opacity-60"
            title="Why am I not seeing real broker funds/data?"
          >
            {diagLoading ? (
              <span className="w-3 h-3 border-2 border-rose-300/40 border-t-rose-300 rounded-full animate-spin"></span>
            ) : (
              <ShieldAlert className="w-3 h-3" />
            )}
            🩺 Diagnose Real-Broker Connection
          </button>
        </div>
        <div className="ticker-track flex gap-8 px-4 py-3 overflow-hidden whitespace-nowrap" style={{ transform: 'translateZ(0)', backfaceVisibility: 'hidden' }}>
          {[...TICKER_SYMBOLS, ...TICKER_SYMBOLS].map((sym, idx) => {
            const t = tickerMap[sym];
            const price = t?.ltp ?? t?.price;
            const chg = t?.change_pct ?? t?.chg_pct ?? 0;
            const up = (chg ?? 0) >= 0;
            return (
              <div key={`${sym}-${idx}`} className="inline-flex items-center gap-2 font-mono">
                <span className="text-slate-400 text-xs tracking-wider">{sym}</span>
                <span className="text-white text-sm font-bold">{price ? fmt(price, sym === 'BTC' || sym === 'ETH' ? 0 : 2) : '—'}</span>
                <span className={`text-xs font-bold flex items-center gap-0.5 ${up ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                  {chg ? `${up ? '+' : ''}${fmt(chg, 2)}%` : '—'}
                </span>
                <span className="text-slate-700">·</span>
              </div>
            );
          })}
        </div>
        <style>{`
          @keyframes terminalScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          .ticker-track { animation: terminalScroll 60s linear infinite; will-change: transform; }
          .ticker-track:hover { animation-play-state: paused; }
          /* Disable hardware-accelerated transform animation on small screens — fixes
             Android Chrome GPU compositor corruption (horizontal scan-line artifacts) */
          @media (max-width: 767px) {
            .ticker-track { animation: none !important; transform: none !important; }
          }
          @media (prefers-reduced-motion: reduce) {
            .ticker-track { animation: none !important; }
          }
        `}</style>
      </div>

      {/* =================== QUICK STATS BAR =================== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="terminal-stats">
        <Stat
          icon={Cpu}
          label="Active Bots"
          value={`${activeBots} / ${totalBots}`}
          accent="text-emerald-300"
          dot="bg-emerald-400"
          testid="stat-active-bots"
        />
        <Stat
          icon={BarChart3}
          label="Capital Deployed"
          value={`₹${fmt(totalCapital, 0)}`}
          accent="text-amber-300"
          dot="bg-amber-400"
          testid="stat-capital"
        />
        <Stat
          icon={IndianRupee}
          label="Today's P&L"
          value={`${todayPnl >= 0 ? '+' : ''}₹${fmt(todayPnl, 2)}`}
          accent={todayPnl >= 0 ? 'text-emerald-300' : 'text-rose-300'}
          dot={todayPnl >= 0 ? 'bg-emerald-400' : 'bg-rose-400'}
          testid="stat-pnl"
        />
        <Stat
          icon={ShieldCheck}
          label="Live Trading"
          value={status?.live_trading_enabled ? 'ENABLED' : 'PAPER ONLY'}
          accent={status?.live_trading_enabled ? 'text-emerald-300' : 'text-amber-300'}
          dot={status?.live_trading_enabled ? 'bg-emerald-400' : 'bg-amber-400'}
          testid="stat-live"
        />
      </div>

      {/* =================== AUTO BOT GRID =================== */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-slate-950/80 p-5">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-300">
              <Rocket className="w-4 h-4" /> Auto-Trading Bot Engine
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold bg-gradient-to-r from-emerald-300 via-amber-300 to-pink-400 bg-clip-text text-transparent">
              Deploy a Trading Bot in 1 Click
            </h2>
            <p className="text-slate-400 text-sm mt-1">
              Bots run on backend tick-loops · Risk-gated · Paper-first, then Live.
            </p>
          </div>
          <button
            onClick={() => setActiveTab && setActiveTab('bots')}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-semibold flex items-center gap-2"
            data-testid="terminal-view-all-bots"
          >
            View All My Bots <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Plan quota indicator */}
        <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="terminal-plan-quota">
          <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${quotaReached ? 'bg-rose-500/15 border-rose-500/40 text-rose-200' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'}`}>
            Plan Quota: {bots.length} / {planBotLimit} bots used
          </span>
          {quotaReached && (
            <button
              onClick={() => setActiveTab && setActiveTab('plans')}
              data-testid="terminal-upgrade-cta"
              className="text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow"
            >
              Upgrade for more bots →
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {AUTO_BOTS.map((bp) => {
            const Icon = bp.icon;
            const existing = findExistingBot(bp);
            const isDeploying = deploying === bp.id;
            return (
              <div
                key={bp.id}
                data-testid={`auto-bot-card-${bp.id}`}
                className={`relative overflow-hidden rounded-2xl border ${bp.border} bg-slate-950/70 p-5 transition hover:shadow-xl hover:${bp.glow} hover:-translate-y-0.5`}
              >
                {/* Accent corner — blur only on tablet+ to avoid Android Chrome GPU layering bug */}
                <div className={`hidden md:block absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br ${bp.accent} opacity-20 blur-2xl`} />
                <div className="relative">
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-2.5 rounded-xl bg-gradient-to-br ${bp.accent} text-white shadow-lg`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    {bp.pro && (
                      <span className="text-[10px] font-bold tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/40 px-2 py-0.5 rounded-full">
                        PRO
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-extrabold text-white">{bp.label}</h3>
                  <div className="text-xs text-slate-400 mt-0.5">{bp.tagline}</div>

                  <div className="grid grid-cols-2 gap-2 mt-4 text-[11px]">
                    <Spec label="Instrument" value={bp.instrument} />
                    <Spec label="Cadence" value={bp.cadence} />
                    <Spec label="Risk" value={bp.risk} />
                    <Spec label="Capital" value={`₹${fmt(bp.capital, 0)}`} />
                  </div>

                  <Sparkline points={sparklines[bp.strategy_id]} testid={`auto-bot-sparkline-${bp.id}`} />

                  {existing ? (
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400">Status</span>
                        <span className={`font-bold flex items-center gap-1 ${existing.is_active ? 'text-emerald-300' : 'text-slate-300'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${existing.is_active ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} />
                          {existing.is_active ? 'RUNNING' : 'PAUSED'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400">Mode</span>
                        <span className="font-bold text-amber-300 uppercase">{existing.mode || 'paper'}</span>
                      </div>
                      <button
                        onClick={() => setActiveTab && setActiveTab('bots')}
                        className={`mt-2 w-full py-2 rounded-lg bg-gradient-to-r ${bp.accent} text-white font-bold text-sm shadow hover:opacity-95`}
                        data-testid={`auto-bot-manage-${bp.id}`}
                      >
                        Manage Bot →
                      </button>
                    </div>
                  ) : quotaReached ? (
                    <button
                      onClick={() => setActiveTab && setActiveTab('plans')}
                      className="mt-5 w-full py-2.5 rounded-lg bg-white/5 border border-amber-500/30 text-amber-200 font-bold text-sm flex items-center justify-center gap-2"
                      data-testid={`auto-bot-locked-${bp.id}`}
                    >
                      <Lock className="w-4 h-4" /> Upgrade to Deploy
                    </button>
                  ) : (
                    <button
                      disabled={isDeploying}
                      onClick={() => deployBot(bp)}
                      className={`mt-5 w-full py-2.5 rounded-lg bg-gradient-to-r ${bp.accent} text-white font-bold text-sm shadow hover:opacity-95 disabled:opacity-60 flex items-center justify-center gap-2`}
                      data-testid={`auto-bot-deploy-${bp.id}`}
                    >
                      {isDeploying ? (
                        <>
                          <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                          Deploying…
                        </>
                      ) : (
                        <>
                          <Power className="w-4 h-4" />
                          Deploy {bp.label}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* =================== TWO-COLUMN: BROKER BRIDGE + EDUCATION =================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ----- Broker Bridges ----- */}
        <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 to-slate-950/80 p-5" data-testid="terminal-broker-bridges">
          <div className="flex items-end justify-between mb-4 gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold uppercase tracking-widest text-violet-300 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Secure Broker Bridges
              </div>
              <h3 className="text-lg sm:text-xl font-extrabold text-white mt-1 break-words">Establish a live connection</h3>
              <p className="text-slate-400 text-xs mt-0.5">Tokens are AES-encrypted at rest · You hold the keys.</p>
            </div>
            <button
              onClick={() => setActiveTab && setActiveTab('apikeys')}
              className="text-xs px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold flex items-center gap-1.5 flex-shrink-0 whitespace-nowrap"
              data-testid="terminal-open-vault"
            >
              <span className="hidden sm:inline">Open API Vault</span><span className="sm:hidden">Vault</span> <ArrowRight className="w-3 h-3" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { id: 'zerodha', name: 'Zerodha Kite', color: 'from-orange-500 to-rose-500' },
              { id: 'upstox', name: 'Upstox', color: 'from-violet-500 to-fuchsia-500' },
              { id: 'binance', name: 'Binance', color: 'from-yellow-500 to-amber-500' },
              { id: 'wazirx', name: 'WazirX', color: 'from-blue-500 to-cyan-500' },
            ].map((b) => {
              const connected = brokers.some(x => (x.broker_name || x.name || '').toLowerCase().includes(b.id));
              return (
                <div
                  key={b.id}
                  data-testid={`bridge-card-${b.id}`}
                  className={`relative rounded-xl border ${connected ? 'border-emerald-500/40' : 'border-white/10'} bg-slate-950/60 p-4 flex items-center justify-between`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${b.color} flex items-center justify-center text-white font-extrabold text-sm`}>
                      {b.name[0]}
                    </div>
                    <div>
                      <div className="text-white font-bold text-sm">{b.name}</div>
                      <div className={`text-[11px] font-semibold flex items-center gap-1 ${connected ? 'text-emerald-300' : 'text-slate-400'}`}>
                        {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                        {connected ? 'Bridge Active' : 'Not Connected'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveTab && setActiveTab('apikeys')}
                    className={`text-xs font-bold px-3 py-1.5 rounded-lg ${connected ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-white/10 hover:bg-white/15 text-white'}`}
                    data-testid={`bridge-cta-${b.id}`}
                  >
                    {connected ? 'Manage' : 'Establish'}
                  </button>
                </div>
              );
            })}
          </div>

          {liveReadiness && !liveReadiness.ready && (
            <div className="mt-4 flex items-start gap-2 text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-bold">Live trading pre-checks pending:</span>{' '}
                {liveReadiness.missing?.join(', ') || 'connect a broker, accept consent, fund wallet.'}
              </div>
            </div>
          )}
        </div>

        {/* ----- Trading Education (Learn Trading) ----- */}
        <div className="rounded-2xl border border-indigo-500/30 bg-gradient-to-br from-indigo-950/60 to-slate-950/80 p-5 relative overflow-hidden" data-testid="terminal-education-hub">
          <div className="hidden md:block absolute -top-10 -right-10 w-40 h-40 rounded-full bg-indigo-500/20 blur-3xl" />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-widest text-indigo-300 flex items-center gap-2">
              <GraduationCap className="w-4 h-4" /> Trading Education
            </div>
            <h3 className="text-xl font-extrabold text-white mt-1">Learn before you Earn</h3>
            <p className="text-slate-300/80 text-sm mt-1">
              Structured lessons · AI Trading Tutor · Quizzes · Earn TSB tokens on every lesson.
            </p>

            <ul className="mt-4 space-y-2 text-sm">
              {[
                'Basics → Technicals → Risk → F&O',
                'Ask the AI Tutor anything, anytime',
                '7-Day & 30-Day learning streak bonuses',
                'Get certified before going Live',
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-200/90">
                  <CheckCircle2 className="w-4 h-4 text-indigo-300 mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>

            <div className="mt-5 grid grid-cols-2 gap-2">
              <Link
                to="/education"
                data-testid="terminal-cta-education"
                className="px-3 py-2.5 rounded-lg bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-sm font-bold flex items-center justify-center gap-1.5 shadow-lg shadow-indigo-500/30"
              >
                <BookOpen className="w-4 h-4" /> Education Hub
              </Link>
              <button
                onClick={() => setActiveTab && setActiveTab('ai-coach')}
                data-testid="terminal-cta-ai-coach"
                className="px-3 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white text-sm font-bold flex items-center justify-center gap-1.5"
              >
                <Brain className="w-4 h-4" /> AI Coach
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =================== QUICK ACTIONS =================== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="terminal-quick-actions">
        <QuickAction icon={Sparkles} label="AI Smart Signals" tone="from-pink-500 to-rose-500" onClick={() => setActiveTab && setActiveTab('smart-signals')} testid="qa-signals" />
        <QuickAction icon={Brain} label="Strategy Match" tone="from-amber-500 to-orange-500" onClick={() => setActiveTab && setActiveTab('matchmaker')} testid="qa-matchmaker" />
        <QuickAction icon={Target} label="Paper Trade" tone="from-emerald-500 to-teal-500" onClick={() => setActiveTab && setActiveTab('paper')} testid="qa-paper" />
        <QuickAction icon={Activity} label="Live Dashboard" tone="from-violet-500 to-purple-600" onClick={() => setActiveTab && setActiveTab('live-dashboard')} testid="qa-live-dashboard" />
      </div>

      {/* =================== DIAGNOSTICS MODAL =================== */}
      {diagOpen && diag && (
        <DiagnosticsModal
          diag={diag}
          onClose={() => setDiagOpen(false)}
          onRetry={runDiagnostics}
          onGoVault={() => { setDiagOpen(false); setActiveTab && setActiveTab('apikeys'); }}
          onGoBots={() => { setDiagOpen(false); setActiveTab && setActiveTab('bots'); }}
        />
      )}
    </div>
  );
};

// ---------- Sub components ----------
const Stat = ({ icon: Icon, label, value, accent, dot, testid }) => (
  <div
    data-testid={testid}
    className="rounded-2xl border border-white/10 bg-slate-950/60 p-3 sm:p-4 flex items-center gap-2 sm:gap-3"
    style={{ transform: 'translateZ(0)' }}
  >
    <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300" />
    </div>
    <div className="min-w-0 flex-1">
      <div className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
        <span className="truncate">{label}</span>
      </div>
      <div className={`text-sm sm:text-lg md:text-xl font-extrabold ${accent} break-words leading-tight`}>{value}</div>
    </div>
  </div>
);

const Spec = ({ label, value }) => (
  <div className="bg-white/5 rounded-md px-2 py-1.5">
    <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-[11px] text-slate-200 font-semibold truncate">{value}</div>
  </div>
);

// ---- Mini SVG sparkline of cumulative 24h P&L ----
const Sparkline = ({ points, testid }) => {
  const W = 220, H = 36, PAD = 2;
  const data = Array.isArray(points) && points.length > 0 ? points : null;

  if (!data) {
    return (
      <div className="mt-3 h-9 flex items-center justify-center text-[10px] text-slate-500 bg-white/5 rounded-md" data-testid={testid}>
        Awaiting 24h trade data…
      </div>
    );
  }

  const last = data[data.length - 1] || 0;
  const up = last >= 0;
  const lo = Math.min(0, ...data);
  const hi = Math.max(0, ...data);
  const range = (hi - lo) || 1;
  const stepX = (W - 2 * PAD) / Math.max(1, data.length - 1);

  const pts = data.map((v, i) => {
    const x = PAD + i * stepX;
    const y = H - PAD - ((v - lo) / range) * (H - 2 * PAD);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  // Zero baseline
  const zeroY = H - PAD - ((0 - lo) / range) * (H - 2 * PAD);
  const stroke = up ? '#34d399' : '#fb7185';
  const fillId = `spark-fill-${testid || Math.random().toString(36).slice(2, 7)}`;

  return (
    <div className="mt-3 relative" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] uppercase tracking-wider text-slate-500">24h P&L</span>
        <span className={`text-[11px] font-bold ${up ? 'text-emerald-300' : 'text-rose-300'}`}>
          {up ? '+' : ''}₹{last.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-9 bg-white/5 rounded-md" preserveAspectRatio="none">
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* Zero baseline */}
        <line x1={PAD} x2={W - PAD} y1={zeroY} y2={zeroY} stroke="rgba(255,255,255,0.12)" strokeDasharray="2 2" />
        {/* Filled area */}
        <polyline
          points={`${PAD},${H - PAD} ${pts} ${W - PAD},${H - PAD}`}
          fill={`url(#${fillId})`}
          stroke="none"
        />
        {/* Line */}
        <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        {/* Last-point dot */}
        {(() => {
          const lx = PAD + (data.length - 1) * stepX;
          const ly = H - PAD - ((last - lo) / range) * (H - 2 * PAD);
          return <circle cx={lx} cy={ly} r="2" fill={stroke} />;
        })()}
      </svg>
    </div>
  );
};

const QuickAction = ({ icon: Icon, label, tone, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`rounded-xl bg-gradient-to-br ${tone} p-4 text-white font-bold text-sm flex flex-col items-start gap-2 shadow hover:shadow-lg hover:-translate-y-0.5 transition`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-left leading-tight">{label}</span>
  </button>
);

// ---- Real-broker Diagnostics Modal ----
const DIAG_TONES = {
  ready:   { headerBg: 'bg-emerald-500/5', iconBg: 'bg-emerald-500/15 border-emerald-500/40', iconColor: 'text-emerald-300', labelText: 'text-emerald-300' },
  partial: { headerBg: 'bg-amber-500/5',   iconBg: 'bg-amber-500/15 border-amber-500/40',   iconColor: 'text-amber-300',   labelText: 'text-amber-300'   },
  blocked: { headerBg: 'bg-rose-500/5',    iconBg: 'bg-rose-500/15 border-rose-500/40',     iconColor: 'text-rose-300',    labelText: 'text-rose-300'    },
};

const DiagnosticsModal = ({ diag, onClose, onRetry, onGoVault, onGoBots }) => {
  const t = DIAG_TONES[diag.overall] || DIAG_TONES.blocked;
  const Icon = diag.overall === 'ready' ? CheckCircle2 : diag.overall === 'partial' ? AlertTriangle : ShieldAlert;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="diagnostics-modal"
    >
      <div
        className="bg-gradient-to-br from-slate-900 to-slate-950 border border-white/10 rounded-2xl w-full max-w-2xl my-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-5 border-b border-white/10 flex items-start gap-4 ${t.headerBg}`}>
          <div className={`p-3 rounded-xl border ${t.iconBg}`}>
            <Icon className={`w-6 h-6 ${t.iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`text-[11px] font-bold uppercase tracking-widest ${t.labelText}`}>
              Real-Broker Connection Diagnostics
            </div>
            <h3 className="text-xl font-extrabold text-white mt-0.5">{diag.headline}</h3>
            <p className="text-slate-400 text-xs mt-1">
              Generated: {diag.generated_at}
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="diagnostics-close"
            className="text-slate-400 hover:text-white p-1"
          >
            ✕
          </button>
        </div>

        {/* Checks */}
        <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {diag.checks.map((c) => {
            const PALETTE = {
              pass: { wrap: 'bg-emerald-500/10 border-emerald-500/30', pill: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30', text: 'text-emerald-300', label: 'PASS', icon: CheckCircle2 },
              fail: { wrap: 'bg-rose-500/10 border-rose-500/40',       pill: 'bg-rose-500/10 text-rose-300 border-rose-500/40',         text: 'text-rose-300',    label: 'FAIL', icon: AlertTriangle },
              warn: { wrap: 'bg-amber-500/10 border-amber-500/30',     pill: 'bg-amber-500/10 text-amber-300 border-amber-500/30',     text: 'text-amber-300',   label: 'WARN', icon: AlertTriangle },
            };
            const p = PALETTE[c.status] || PALETTE.fail;
            const CIcon = p.icon;
            return (
              <div
                key={c.id}
                data-testid={`diag-check-${c.id}`}
                className={`rounded-xl border p-3 ${p.wrap}`}
              >
                <div className="flex items-start gap-3">
                  <CIcon className={`w-5 h-5 ${p.text} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-white text-sm">{c.label}</span>
                      <span className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full border ${p.pill}`}>
                        {p.label}
                      </span>
                    </div>
                    <div className="text-slate-300/90 text-xs mt-1">{c.detail}</div>
                    {c.fix && (
                      <div className="mt-2 text-[11px] text-slate-200/80 bg-black/30 rounded-md px-2 py-1.5 border border-white/5">
                        <span className="font-bold text-amber-300">Fix:</span> {c.fix}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/10 flex flex-wrap gap-2 justify-end bg-black/30">
          <button
            onClick={onGoVault}
            data-testid="diagnostics-go-vault"
            className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-bold"
          >
            Open API Vault
          </button>
          <button
            onClick={onGoBots}
            data-testid="diagnostics-go-bots"
            className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-xs font-bold"
          >
            Open My Bots
          </button>
          <button
            onClick={onRetry}
            data-testid="diagnostics-retry"
            className="px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold"
          >
            Re-run Diagnostics
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradingTerminal;
